/**
 * CK AI content overlay: scans every <img alt="..."> on the page (any section, current
 * or future) and, when the ALT text contains the keyword configured in Theme settings >
 * CK | Image AI Content, injects the disclosure label into the nearest positioned
 * ancestor — never the image itself, and we never create/move elements to build one:
 * reparenting the <img> risks breaking other code that depends on its original DOM
 * position (CSS like `.foo > img`, sibling selectors, other scripts reading
 * img.parentElement, etc.).
 *
 * The label's on-screen position is plain CSS (see ck-image-ai-content.css): a default
 * bottom-right inset that works whenever the host container is roughly the same size as
 * the image. For sections where that default doesn't land correctly (the host is a
 * different size/shape than the photo — offset/stacked image layouts, hero banners with
 * parallax or object-fit crops, etc.), that section's own "CK | Change AI content
 * position" setting exposes two sliders that override the position via CSS custom
 * properties (--ck-ai-content-right / --ck-ai-content-bottom) set on the section — since
 * custom properties inherit, any label rendered anywhere inside that section picks up the
 * override automatically, no JS changes needed per section.
 *
 * Runs once on load, then a MutationObserver catches images added later (AJAX cart,
 * quick view, section re-renders, infinite scroll, etc.) without a full page re-scan.
 */
(function () {
  if (window.__ckImageAiContentInit) return;
  window.__ckImageAiContentInit = true;

  var processed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var processedFallback = [];

  function isProcessed(img) {
    if (processed) return processed.has(img);
    return processedFallback.indexOf(img) !== -1;
  }

  function markProcessed(img) {
    if (processed) {
      processed.add(img);
    } else {
      processedFallback.push(img);
    }
  }

  // Elements with a strict/managed content model — never append into these, even as an
  // unrelated extra child. Appending into <picture> in particular was observed breaking
  // this theme's `is="animate-picture"` reveal animation (assets/theme.js), which expects
  // picture's only children to be <source>/<img>.
  var UNSAFE_HOST_TAGS = { PICTURE: true, SELECT: true, TABLE: true, VIDEO: true, AUDIO: true };

  function nearestPositionedAncestor(img) {
    var el = img.parentElement;
    while (el && el !== document.body) {
      if (!UNSAFE_HOST_TAGS[el.tagName]) {
        var position = window.getComputedStyle(el).position;
        if (position === 'relative' || position === 'absolute' || position === 'fixed' || position === 'sticky') {
          return el;
        }
      }
      el = el.parentElement;
    }
    return null;
  }

  function addLabel(img, config) {
    if (isProcessed(img)) return;
    markProcessed(img);

    var alt = (img.getAttribute('alt') || '').toLowerCase();
    if (alt.indexOf(config.tag) === -1) return;

    // Carousels (e.g. this theme's product-card hover-swap, built on Flickity) clone a
    // slide for infinite wrap-around looping, marking the clone aria-hidden="true" — but
    // the clone keeps the same alt text as the real slide. Without this check the clone
    // gets labeled too, showing a second overlapping label during the swipe transition.
    if (img.closest('[aria-hidden="true"]')) return;

    // Product media gallery thumbnails (snippets/product-thumbnail.liquid) reuse the same
    // alt text as the main photo they navigate to, so a thumbnail of an AI-tagged image
    // matches the tag too — but the label doesn't make sense crammed onto a ~90px nav
    // button. Skip it there; the full-size image it points to still gets labeled normally.
    if (img.closest('.product__thumbnail')) return;

    // This theme's product-card hover-swap carousel (<secondary-media>) duplicates the
    // card's default image as its first slide, so the exact same photo (same alt text)
    // can exist twice in one card — once as the standalone default <img>, once inside
    // the carousel. If a copy outside the carousel already exists, this is just that
    // duplicate: skip it, so the label doesn't render twice during the hover swipe.
    var secondaryMediaAncestor = img.closest('secondary-media');
    if (secondaryMediaAncestor) {
      var cardScope = img.closest('.product-card') || img.closest('.card') || document.body;
      var hasStandaloneCopy = Array.prototype.some.call(cardScope.querySelectorAll('img[alt]'), function (other) {
        return other !== img && !other.closest('secondary-media') && (other.getAttribute('alt') || '').toLowerCase() === alt;
      });
      if (hasStandaloneCopy) return;
    }

    var host = nearestPositionedAncestor(img);
    if (!host) return;

    var label = document.createElement('span');
    label.className = 'ck-image-ai-content';
    label.style.color = config.color;
    label.textContent = config.text;
    host.appendChild(label);
  }

  function scan(root, config) {
    var images = root.querySelectorAll ? root.querySelectorAll('img[alt]') : [];
    for (var i = 0; i < images.length; i++) {
      addLabel(images[i], config);
    }
  }

  function init() {
    var settings = (window.theme && window.theme.ckImageAiContent) || {};
    var config = {
      tag: (settings.tag || '').trim().toLowerCase(),
      text: settings.text || '',
      color: settings.color || '#ffffff',
    };

    if (!config.tag || !config.text) return;

    scan(document, config);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            addLabel(node, config);
          } else if (node.querySelectorAll) {
            scan(node, config);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
